import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import {
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaAngleLeft,
    FaAngleRight,
    FaBold,
    FaEdit,
    FaHeading,
    FaInfoCircle,
    FaListUl,
    FaParagraph,
    FaPlus,
    FaSave,
    FaSearch,
    FaTrash,
    FaTimes,
    FaUpload,
} from "react-icons/fa";
import Swal from "sweetalert2";

import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import { clearNewsPostCache } from "../utils/newsPosts";
import { notifyUser } from "../utils/notifyUser";

const emptyPost = {
    title: "",
    excerpt: "",
    image: "",
    blogImage: "",
    videoUrl: "",
    status: "published",
    publishedAt: "",
    content: [
        {
            type: "paragraph",
            text: "",
        },
    ],
};

const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/news-posts`;
const NEWS_IMAGE_WIDTH = 1200;
const NEWS_IMAGE_HEIGHT = 675;
const NEWS_IMAGE_ASPECT = NEWS_IMAGE_WIDTH / NEWS_IMAGE_HEIGHT;
const BLOG_IMAGE_WIDTH = 1920;
const BLOG_IMAGE_HEIGHT = 560;
const BLOG_IMAGE_ASPECT = BLOG_IMAGE_WIDTH / BLOG_IMAGE_HEIGHT;
const formatImageSize = ({ width, height }) => `${width} x ${height}px`;

const getVideoUrlError = (value) => {
    const videoUrl = String(value || "").trim();

    if(!videoUrl) {
        return "";
    }

    if(videoUrl.length > 2048) {
        return "Video URL must be 2,048 characters or fewer";
    }

    try {
        const parsedUrl = new URL(videoUrl);
        const hostname = parsedUrl.hostname.toLowerCase();
        const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
        const isHttp = ["http:", "https:"].includes(parsedUrl.protocol);
        const isYouTube =
            hostname === "youtu.be" ||
            hostname === "youtube.com" ||
            hostname.endsWith(".youtube.com") ||
            hostname === "youtube-nocookie.com" ||
            hostname.endsWith(".youtube-nocookie.com");
        const youtubeId = hostname === "youtu.be"
            ? pathSegments[0]
            : parsedUrl.searchParams.get("v") ||
              (["embed", "shorts", "live"].includes(pathSegments[0])
                  ? pathSegments[1]
                  : "");
        const isVimeo =
            hostname === "vimeo.com" || hostname.endsWith(".vimeo.com");
        const vimeoId = [...pathSegments]
            .reverse()
            .find((segment) => /^\d+$/.test(segment));

        return isHttp && ((isYouTube && youtubeId) || (isVimeo && vimeoId))
            ? ""
            : "Enter a valid YouTube or Vimeo video URL";
    } catch{
        return "Enter a valid YouTube or Vimeo video URL";
    }
};

const imageCropConfigs = {
    image: {
        label: "News Image",
        width: NEWS_IMAGE_WIDTH,
        height: NEWS_IMAGE_HEIGHT,
        aspect: NEWS_IMAGE_ASPECT,
    },
    blogImage: {
        label: "Blog Image",
        width: BLOG_IMAGE_WIDTH,
        height: BLOG_IMAGE_HEIGHT,
        aspect: BLOG_IMAGE_ASPECT,
    },
};

const renderImageSizeTooltip = (size) => (
    <span className="group relative inline-flex items-center">
        <FaInfoCircle
            className="text-navy"
            tabIndex={0}
            aria-label={`Recommended size: ${size}`}
        />
        <span className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block">
            Recommended size: {size}
        </span>
    </span>
);

const blocksToHtml = (blocks = []) =>
    blocks
        .map((block) => {
            const blockText = block.text || "";

            if(block.type === "html") {
                return blockText;
            }

            if(block.type === "heading") {
                return `<h2>${blockText}</h2>`;
            }

            if(block.type === "subheading") {
                return `<h3>${blockText}</h3>`;
            }

            if(block.type === "bold") {
                return `<p><strong>${blockText}</strong></p>`;
            }

            if(block.type === "list" && Array.isArray(block.items)) {
                return `<ul>${block.items
                    .map((item) => `<li>${item}</li>`)
                    .join("")}</ul>`;
            }

            if(blockText) {
                return `<p>${blockText}</p>`;
            }

            return "";
        })
        .filter(Boolean)
        .join("");

const sanitizeHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || "", "text/html");
    doc.querySelectorAll("script,style,iframe,object,embed").forEach((node) => {
        node.remove();
    });
    doc.body.querySelectorAll("*").forEach((node) => {
        Array.from(node.attributes).forEach((attribute) => {
            const name = attribute.name.toLowerCase();
            const value = attribute.value.trim().toLowerCase();

            if(name.startsWith("on") || value.startsWith("javascript:")) {
                node.removeAttribute(attribute.name);
            }
        });
    });

    return doc.body.innerHTML.trim();
};

const getPreviewUrl = (image) => {
    if(!image) {
        return "";
    }

    if(image.startsWith("/uploads/")) {
        return `${process.env.REACT_APP_BACKEND_URL}${image}`;
    }

    if(image.startsWith("/api/images/")) {
        return image.replace(/^\/api\/images\//, "/images/");
    }

    return image;
};

const getCroppedImage = (imageSrc, pixelCrop, dimensions) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = dimensions.width;
            canvas.height = dimensions.height;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(
                image,
                pixelCrop.x,
                pixelCrop.y,
                pixelCrop.width,
                pixelCrop.height,
                0,
                0,
                dimensions.width,
                dimensions.height,
            );

            canvas.toBlob(
                (blob) => {
                    if(!blob) {
                        reject(new Error("Unable to crop image"));
                        return;
                    }

                    resolve(blob);
                },
                "image/jpeg",
                0.92,
            );
        };
        image.onerror = () => reject(new Error("Unable to load image"));
        image.src = imageSrc;
    });

const NewsPostManagement = () => {
    const [posts, setPosts] = useState([]);
    const [selectedPost, setSelectedPost] = useState(null);
    const [previewPost, setPreviewPost] = useState(null);
    const [form, setForm] = useState(emptyPost);
    const [contentHtml, setContentHtml] = useState(
        blocksToHtml(emptyPost.content),
    );
    const [editorRevision, setEditorRevision] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppingImage, setCroppingImage] = useState("");
    const [croppingFileName, setCroppingFileName] = useState("");
    const [croppingField, setCroppingField] = useState("image");
    const [showCropper, setShowCropper] = useState(false);
    const [activeFormats, setActiveFormats] = useState({
        block: "p",
        bold: false,
        bulletList: false,
    });
    const editorRef = useRef(null);

    const filteredPosts = useMemo(() => {
        const query = searchTerm.toLowerCase();
        return posts.filter(
            (post) =>
                post.title.toLowerCase().includes(query) ||
                post.excerpt.toLowerCase().includes(query) ||
                post.status.toLowerCase().includes(query),
        );
    }, [posts, searchTerm]);

    const totalRecords = filteredPosts.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / perPage));
    const paginatedPosts = filteredPosts.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );
    const fromRecord = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(currentPage * perPage, totalRecords);

    useEffect(() => {
        if(currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const loadPosts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(apiUrl, { withCredentials: true });
            setPosts(sortByAuditUpdatedOn(response.data.posts || []));
        } catch{
            notifyUser(null, "error", "Unable to load news posts", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPosts();
    }, []);

    useEffect(() => {
        if(editorRef.current) {
            editorRef.current.innerHTML = contentHtml;
        }
    }, [editorRevision]);

    useEffect(
        () => () => {
            if(croppingImage?.startsWith("blob:")) {
                URL.revokeObjectURL(croppingImage);
            }
        },
        [croppingImage],
    );

    const resetForm = () => {
        setSelectedPost(null);
        setForm(emptyPost);
        setContentHtml(blocksToHtml(emptyPost.content));
        setEditorRevision((current) => current + 1);
    };

    const openNewPostModal = () => {
        resetForm();
        setIsEditorOpen(true);
    };

    const closeEditorModal = () => {
        setIsEditorOpen(false);
        resetForm();
    };

    const getPostContentHtml = (post) =>
        sanitizeHtml(
            blocksToHtml(post?.content?.length ? post.content : emptyPost.content),
        );

    const handleSelect = (post) => {
        setSelectedPost(post);
        setForm({
            title: post.title || "",
            excerpt: post.excerpt || "",
            image: post.image || "",
            blogImage: post.blogImage || post.image || "",
            videoUrl: post.videoUrl || "",
            status: post.status || "published",
            publishedAt: post.publishedAt || "",
        });
        setContentHtml(
            blocksToHtml(post.content?.length ? post.content : emptyPost.content),
        );
        setEditorRevision((current) => current + 1);
        setIsEditorOpen(true);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((current) => ({
            ...current,
            [name]: value,
        }));
    };

    const handleImageUpload = async (event, fieldName) => {
        const file = event.target.files?.[0];
        if(!file) {
            return;
        }

        if(!file.type.startsWith("image/")) {
            notifyUser(null, "error", "Please upload an image file", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(croppingImage?.startsWith("blob:")) {
            URL.revokeObjectURL(croppingImage);
        }

        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setCroppingFileName(file.name);
        setCroppingField(fieldName);
        setCroppingImage(URL.createObjectURL(file));
        setShowCropper(true);
        event.target.value = "";
    };

    const handleCropCancel = () => {
        setShowCropper(false);
        if(croppingImage?.startsWith("blob:")) {
            URL.revokeObjectURL(croppingImage);
        }
        setCroppingImage("");
        setCroppingFileName("");
        setCroppedAreaPixels(null);
    };

    const handleCropSave = async () => {
        if(!croppingImage || !croppedAreaPixels) {
            notifyUser(null, "error", "Please select an image crop", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setUploadingImage(true);

        try {
            const cropConfig = imageCropConfigs[croppingField];
            const croppedBlob = await getCroppedImage(
                croppingImage,
                croppedAreaPixels,
                cropConfig,
            );
            const baseName = croppingFileName.replace(/\.[^.]+$/, "");
            const fileName = `${baseName || "news-post"}.jpg`;
            const formData = new FormData();
            formData.append("image", croppedBlob, fileName);

            const response = await axios.post(`${apiUrl}/image`, formData, {
                withCredentials: true,
            });
            setForm((current) => ({
                ...current,
                [croppingField]: response.data.image,
            }));
            notifyUser(null, "success", `${cropConfig.label} cropped and uploaded`, "toast", null, null, null, null, null, false, false, "top-center");
            handleCropCancel();
        } catch(error) {
            notifyUser(null, "error", error.response?.data?.error || "Unable to upload image", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRemoveImage = (fieldName) => {
        setForm((current) => ({
            ...current,
            [fieldName]: "",
        }));
    };

    const renderImageControl = (fieldName) => {
        const cropConfig = imageCropConfigs[fieldName];
        const imageValue = form[fieldName];

        return (
            <div>
                <span className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700">
                    {cropConfig.label}
                    {renderImageSizeTooltip(formatImageSize(cropConfig))}
                </span>
                <label className="flex items-center justify-center gap-2 w-full border border-dashed border-[#C2D7E2] rounded px-3 py-2 text-navy cursor-pointer hover:border-navy hover:bg-[#F5F7FA]">
                    <FaUpload />
                    {imageValue ? "Add New Image" : "Upload Image"}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(event) =>
                            handleImageUpload(event, fieldName)
                        }
                        disabled={uploadingImage}
                        className="hidden"
                    />
                </label>
                {imageValue && imageValue !== "/api/images/" && (
                    <div className="mt-3">
                        <img
                            src={getPreviewUrl(imageValue)}
                            alt={`${cropConfig.label} preview`}
                            style={{
                                aspectRatio: `${cropConfig.width} / ${cropConfig.height}`,
                            }}
                            className="w-full rounded object-cover border border-gray-200"
                        />
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                            <button
                                type="button"
                                onClick={() => handleRemoveImage(fieldName)}
                                className="inline-flex items-center gap-2 rounded border border-red-200 px-3 py-1 text-sm text-red-600 hover:bg-red-50"
                            >
                                <FaTrash />
                                Remove
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const updateActiveFormats = () => {
        if(!editorRef.current || !document.activeElement) {
            return;
        }

        const selection = window.getSelection();
        if(
            !selection?.rangeCount ||
            !editorRef.current.contains(selection.anchorNode)
        ) {
            return;
        }

        const blockValue = document.queryCommandValue("formatBlock");
        const normalizedBlock = String(blockValue || "p")
            .replace(/[<>]/g, "")
            .toLowerCase();

        setActiveFormats({
            block: normalizedBlock || "p",
            bold: document.queryCommandState("bold"),
            bulletList: document.queryCommandState("insertUnorderedList"),
        });
    };

    const runEditorCommand = (command, value = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, value);
        setContentHtml(editorRef.current?.innerHTML || "");
        updateActiveFormats();
    };

    const getToolbarButtonClass = (isActive) =>
        `inline-flex items-center gap-2 rounded border px-3 py-2 text-sm ${
            isActive
                ? "border-navy bg-navy text-white"
                : "border-[#C2D7E2] bg-white text-navy"
        }`;

    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {fromRecord} to {toRecord} of {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    const handleExportPostsCSV = () => {
        if(!filteredPosts.length) {
            notifyUser(null, "info", "No posts to export", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const rows = filteredPosts.map((post) => ({
            Title: post.title || "",
            Excerpt: post.excerpt || "",
            Status: post.status || "",
            Version: post.version || "",
            PublishedAt: post.publishedAt
                ? new Date(post.publishedAt).toLocaleString()
                : "",
            VideoUrl: post.videoUrl || "",
        }));

        const header = Object.keys(rows[0]).join(",");
        const csvBody = rows
            .map((row) => {
                return Object.values(row)
                    .map((value) => `"${String(value).replace(/"/g, '""')}"`)
                    .join(",");
            })
            .join("\n");

        const csv = `${header}\n${csvBody}`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `news-posts-${new Date().toISOString().slice(0, 10)}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleSave = async () => {
        const currentContentHtml = editorRef.current?.innerHTML || contentHtml;
        const sanitizedContent = sanitizeHtml(currentContentHtml);
        const parsedContent = sanitizedContent
            ? [{ type: "html", text: sanitizedContent }]
            : [];

        if(!form.title.trim() || !form.excerpt.trim()) {
            notifyUser(null, "error", "Title and excerpt are required", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(parsedContent.length === 0) {
            notifyUser(null, "error", "Add at least one content block", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const videoUrlError = getVideoUrlError(form.videoUrl);
        if(videoUrlError) {
            notifyUser(null, "error", videoUrlError, "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setSaving(true);

        try {
            const payload = {
                ...form,
                heroTitle: form.title,
                publishedAt: form.publishedAt || null,
                content: parsedContent,
            };

            if(selectedPost?.id) {
                await axios.put(`${apiUrl}/${selectedPost.id}`, payload, {
                    withCredentials: true,
                });
            } else {
                await axios.post(apiUrl, payload, { withCredentials: true });
            }

            clearNewsPostCache();
            notifyUser(null, "success", "News post saved", "toast", null, null, null, null, null, false, false, "top-center");
            await loadPosts();
            resetForm();
            setIsEditorOpen(false);
        } catch(error) {
            notifyUser(null, "error", error.response?.data?.error || "Unable to save post", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (post) => {
        const result = await Swal.fire({
            title: "Delete news post?",
            text: `This will remove "${post.title}" from the site.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "var(--navy)",
            confirmButtonText: "Delete",
        });

        if(!result.isConfirmed) {
            return;
        }

        try {
            await axios.delete(`${apiUrl}/${post.id}`, {
                withCredentials: true,
            });
            clearNewsPostCache();
            notifyUser(null, "success", "News post deleted", "toast", null, null, null, null, null, false, false, "top-center");
            await loadPosts();
            if(selectedPost?.id === post.id) {
                closeEditorModal();
            }
        } catch{
            notifyUser(null, "error", "Unable to delete post", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <div className="aqi-grid-page w-[95%] mx-auto my-8 bg-white rounded-lg shadow-lg p-6 min-h-[80vh]">
            <div className="mb-6">
                <div>
                    <h2
                        style={{
                            color: "var(--navy)",
                            marginBottom: "20px",
                            fontSize: "large",
                            fontFamily: "large",
                        }}
                    >
                NEWS & BLOGS MANAGEMENT
            </h2>
                </div>
            </div>

            <div>
                <section className="flex h-full flex-col rounded border border-gray-200 p-4">
                    <div className="aqi-grid-toolbar">
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={perPage}
                            onChange={(event) => {
                                setPerPage(Number(event.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            {GRID_CONFIG.pageSizeOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>

                        <div className="aqi-grid-search">
                                <FaSearch className="absolute left-3 top-3 text-navy" />
                                <input
                                    value={searchTerm}
                                    onChange={(event) => {
                                        setSearchTerm(event.target.value);
                                        setCurrentPage(1);
                                    }}
                                    placeholder="Search posts..."
                                    className="aqi-grid-search-input w-full border-2 border-[#C2D7E2] rounded pl-10 pr-3 focus:outline-none focus:border-navy"
                                />
                        </div>
                        <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                            <button
                                onClick={openNewPostModal}
                                className="aqi-grid-button aqi-grid-button--primary"
                            >
                                <FaPlus />
                                Add Post
                            </button>
                            <button
                                onClick={handleExportPostsCSV}
                                className="aqi-grid-button aqi-grid-button--primary"
                                title="Export CSV"
                            >
                                Export
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-x-auto rounded border border-gray-200">
                        <table className="min-w-full text-sm">
                            <thead className="bg-navy text-white">
                                <tr>
                                    <th className="text-left p-3">Title</th>
                                    <th className="text-left p-3">Status</th>
                                    <th className="text-left p-3">Version</th>
                                    <th className="text-right p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td className="p-4" colSpan={4}>
                                            Loading posts...
                                        </td>
                                    </tr>
                                ) : filteredPosts.length === 0 ? (
                                    <tr>
                                        <td className="p-4" colSpan={4}>
                                            No posts found.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedPosts.map((post) => (
                                        <tr
                                            key={post.id}
                                            className={`border-t hover:bg-[#f1f8ff] ${
                                                selectedPost?.id === post.id
                                                    ? "bg-[#C2D7E2]/40"
                                                    : ""
                                            }`}
                                        >
                                            <td className="p-4 font-medium">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setPreviewPost(post)
                                                    }
                                                    className="text-left text-navy hover:text-navy-2 hover:underline"
                                                    title="View post"
                                                >
                                                    {post.title}
                                                </button>
                                            </td>
                                            <td className="p-4 capitalize">
                                                {post.status}
                                            </td>
                                            <td className="p-4">
                                                {post.version}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() =>
                                                            handleSelect(post)
                                                        }
                                                        className="text-navy hover:text-navy-2"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(post)
                                                        }
                                                        className="text-red-600 hover:text-red-800"
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <CustomPagination />
                </section>
            </div>

            {previewPost && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-6">
                    <section className="flex max-h-[calc(100vh-3rem)] w-full max-w-6xl flex-col rounded-lg bg-white p-5 shadow-2xl">
                        <div className="mb-4 flex items-start justify-between gap-4 border-b border-gray-200 pb-3">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg font-semibold text-navy">
                                    {previewPost.title}
                                </h2>
                                <p className="mt-1 text-sm capitalize text-gray-500">
                                    {previewPost.status}{" "}
                                    {previewPost.version
                                        ? `| Version ${previewPost.version}`
                                        : ""}
                                </p>
                            </div>
                            <button
                                onClick={() => setPreviewPost(null)}
                                className="mt-0.5 inline-flex shrink-0 items-center gap-2 text-gray-600 hover:text-gray-900"
                                title="Close"
                            >
                                <FaTimes />
                                Close
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2">
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">
                                    Excerpt
                                </span>
                                <textarea
                                    readOnly
                                    value={previewPost.excerpt || ""}
                                    rows={3}
                                    className="w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 text-navy focus:outline-none"
                                />
                            </label>

                            {previewPost.videoUrl && (
                                <div className="mt-4">
                                    <span className="mb-1 block text-sm font-medium text-gray-700">
                                        Blog Video
                                    </span>
                                    <a
                                        href={previewPost.videoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="break-all text-sm font-medium text-navy underline"
                                    >
                                        {previewPost.videoUrl}
                                    </a>
                                </div>
                            )}

                            <div className="mt-4 grid gap-4 md:grid-cols-2">
                                {previewPost.image && (
                                    <div>
                                        <span className="mb-1 block text-sm font-medium text-gray-700">
                                            News Image
                                        </span>
                                        <img
                                            src={getPreviewUrl(previewPost.image)}
                                            alt="News preview"
                                            className="w-full rounded border border-gray-200 object-cover"
                                            style={{
                                                aspectRatio: `${NEWS_IMAGE_WIDTH} / ${NEWS_IMAGE_HEIGHT}`,
                                            }}
                                        />
                                    </div>
                                )}
                                {(previewPost.blogImage ||
                                    previewPost.image) && (
                                    <div>
                                        <span className="mb-1 block text-sm font-medium text-gray-700">
                                            Blog Image
                                        </span>
                                        <img
                                            src={getPreviewUrl(
                                                previewPost.blogImage ||
                                                    previewPost.image,
                                            )}
                                            alt="Blog preview"
                                            className="w-full rounded border border-gray-200 object-cover"
                                            style={{
                                                aspectRatio: `${BLOG_IMAGE_WIDTH} / ${BLOG_IMAGE_HEIGHT}`,
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mt-4">
                                <span className="mb-1 block text-sm font-medium text-gray-700">
                                    Article Content
                                </span>
                                <div
                                    className="max-h-[320px] overflow-y-auto rounded border border-gray-300 bg-gray-50 px-4 py-3 text-navy prose prose-sm max-w-none [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-navy-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-navy [&_ul]:list-disc [&_ul]:pl-6"
                                    dangerouslySetInnerHTML={{
                                        __html: getPostContentHtml(previewPost),
                                    }}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            )}

            {isEditorOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-6">
                    <section className="flex max-h-[calc(100vh-3rem)] w-full max-w-5xl flex-col rounded-lg bg-white p-5 shadow-2xl">
                        <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
                            <h2 className="text-lg font-semibold text-navy">
                                {selectedPost ? "Edit Post" : "New Post"}
                            </h2>
                            <button
                                onClick={closeEditorModal}
                                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                                title="Close"
                            >
                                <FaTimes />
                                Close
                            </button>
                        </div>

                        <div className="grid flex-1 gap-4 overflow-y-auto pr-2 md:grid-cols-2">
                        <label className="md:col-span-2">
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                Title
                            </span>
                            <input
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-navy"
                            />
                        </label>

                        <label className="md:col-span-2">
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                Excerpt
                            </span>
                            <textarea
                                name="excerpt"
                                value={form.excerpt}
                                onChange={handleChange}
                                rows={3}
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-navy"
                            />
                        </label>

                        <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
                            {renderImageControl("image")}
                            {renderImageControl("blogImage")}
                        </div>

                        <label className="md:col-span-2">
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                Blog Video URL (optional)
                            </span>
                            <input
                                name="videoUrl"
                                type="url"
                                value={form.videoUrl || ""}
                                onChange={handleChange}
                                maxLength={2048}
                                placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                                aria-invalid={Boolean(getVideoUrlError(form.videoUrl))}
                                className={`w-full rounded border px-3 py-2 focus:outline-none ${
                                    getVideoUrlError(form.videoUrl)
                                        ? "border-red-500 focus:border-red-500"
                                        : "border-gray-300 focus:border-navy"
                                }`}
                            />
                            {getVideoUrlError(form.videoUrl) ? (
                                <span className="mt-1 block text-xs text-red-600">
                                    {getVideoUrlError(form.videoUrl)}
                                </span>
                            ) : (
                                <span className="mt-1 block text-xs text-gray-500">
                                    Supports YouTube and Vimeo links only.
                                </span>
                            )}
                        </label>

                        <label>
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                Published At
                            </span>
                            <input
                                name="publishedAt"
                                type="datetime-local"
                                value={form.publishedAt || ""}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-navy"
                            />
                        </label>

                        <label>
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </span>
                            <select
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-navy"
                            >
                                <option value="published">Published</option>
                                <option value="draft">Draft</option>
                            </select>
                        </label>

                        <div className="md:col-span-2">
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                Article Content
                            </span>
                            <div className="rounded border border-gray-300 bg-white">
                                <div className="flex flex-wrap gap-2 border-b border-gray-200 bg-gray-50 p-2">
                                    <button
                                        type="button"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() =>
                                            runEditorCommand("formatBlock", "p")
                                        }
                                        className={getToolbarButtonClass(
                                            activeFormats.block === "p",
                                        )}
                                    >
                                        <FaParagraph />
                                        Paragraph
                                    </button>
                                    <button
                                        type="button"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() =>
                                            runEditorCommand("formatBlock", "h2")
                                        }
                                        className={getToolbarButtonClass(
                                            activeFormats.block === "h2",
                                        )}
                                    >
                                        <FaHeading />
                                        Heading
                                    </button>
                                    <button
                                        type="button"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() =>
                                            runEditorCommand(
                                                "formatBlock",
                                                "h3",
                                            )
                                        }
                                        className={getToolbarButtonClass(
                                            activeFormats.block === "h3",
                                        )}
                                    >
                                        <FaHeading />
                                        Subheading
                                    </button>
                                    <button
                                        type="button"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() =>
                                            runEditorCommand("bold")
                                        }
                                        className={getToolbarButtonClass(
                                            activeFormats.bold,
                                        )}
                                    >
                                        <FaBold />
                                        Bold
                                    </button>
                                    <button
                                        type="button"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() =>
                                            runEditorCommand(
                                                "insertUnorderedList",
                                            )
                                        }
                                        className={getToolbarButtonClass(
                                            activeFormats.bulletList,
                                        )}
                                    >
                                        <FaListUl />
                                        Bullets
                                    </button>
                                </div>
                                <div
                                    key={editorRevision}
                                    ref={editorRef}
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={(event) => {
                                        setContentHtml(
                                            event.currentTarget.innerHTML,
                                        );
                                        updateActiveFormats();
                                    }}
                                    onClick={updateActiveFormats}
                                    onKeyUp={updateActiveFormats}
                                    className="h-[320px] w-full overflow-y-auto px-4 py-3 text-navy outline-none prose prose-sm max-w-none [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-navy-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-navy [&_ul]:list-disc [&_ul]:pl-6"
                                />
                            </div>
                        </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                onClick={closeEditorModal}
                                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="aqi-primary-action inline-flex items-center gap-2 rounded bg-navy px-4 py-2 text-white hover:bg-navy-2 disabled:opacity-60"
                            >
                                <FaSave />
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </section>
                </div>
            )}
            {showCropper && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4">
                    <div className="flex h-[620px] w-full max-w-4xl flex-col rounded bg-white p-4 shadow-xl">
                        <div className="mb-3">
                            <h3 className="text-lg font-semibold text-navy">
                                Crop {imageCropConfigs[croppingField].label}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {imageCropConfigs[croppingField].width} x{" "}
                                {imageCropConfigs[croppingField].height}
                            </p>
                        </div>
                        <div className="relative flex-1 overflow-hidden rounded bg-black">
                            <Cropper
                                image={croppingImage}
                                crop={crop}
                                zoom={zoom}
                                aspect={imageCropConfigs[croppingField].aspect}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(croppedArea, croppedPixels) => {
                                    setCroppedAreaPixels(croppedPixels);
                                }}
                            />
                        </div>
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Zoom
                            </label>
                            <input
                                type="range"
                                min="1"
                                max="3"
                                step="0.1"
                                value={zoom}
                                onChange={(event) =>
                                    setZoom(Number(event.target.value))
                                }
                                className="w-full accent-navy"
                            />
                        </div>
                        <div className="mt-4 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCropCancel}
                                className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCropSave}
                                disabled={uploadingImage}
                                className="aqi-primary-action rounded bg-navy px-4 py-2 text-white hover:bg-navy-2 disabled:opacity-60"
                            >
                                {uploadingImage ? "Uploading..." : "Save Crop"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsPostManagement;
